import { useEffect, useRef, useState } from 'react'
import { lightTheme } from '@/customTheme'
import { useTranslation } from 'react-i18next'
import ModalHeader from '@/components/ModalHeader'
import { useStyletron } from 'styletron-react'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import SignInWithEmailVerifyCode from './SignInWithEmailVerifyCode'
import { SignInManager } from '../../SignInManager'
import { isValidEmail, getInputStyles, errorMessageStyles } from '../signInStyles'
import { UserInfo } from '@/common/interfaces'

interface SignInWithEmailScreenProps {
  onBack: () => void
  onSuccess: (user: UserInfo) => void
  onSignInWithPassword?: () => void
}

function SignInWithEmailScreen({ onBack, onSuccess, onSignInWithPassword }: SignInWithEmailScreenProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showVerifyCode, setShowVerifyCode] = useState(false)
  const [css] = useStyletron()

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const clearError = () => error && setError('')

  const handleGetMagicCode = async () => {
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      await SignInManager.getInstance().loginWithEmail(email)
      setShowVerifyCode(true)
    } catch (err) {
      console.error('Error sending magic code:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDisabled = !email || !!error
  const inputStyles = getInputStyles({ hasError: !!error, isDisabled: isSubmitting })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {!showVerifyCode ? (
        <>
          <ModalHeader
            hasNavigation
            handleNavigation={onBack}
            title={t('Continue with Email')}
            style={{ padding: 0 }}
            titleStyle={{ textTransform: 'none', marginLeft: '-20px' }}
          />
          <h3
            style={{
              marginBottom: 0,
              marginTop: '8px',
              ...lightTheme.typography.Small14regular,
              fontWeight: 400,
              color: lightTheme.colors.blackGray
            }}
          >
            {t("We'll check if you have a Bazaart account, and create one if you don't.")}
          </h3>

          {/* Email Input */}
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); clearError() }}
            placeholder={t('Enter your email address')}
            disabled={isSubmitting}
            className={css({
              ...inputStyles,
              marginTop: '32px',
              marginBottom: error ? '0' : '24px',
            } as any)}
            onKeyPress={e => e.key === 'Enter' && !isDisabled && handleGetMagicCode()}
          />

          {/* Error Message */}
          {error && <p style={errorMessageStyles}>{t(error)}</p>}

          {/* Get Magic Code Button */}
          <ButtonCustom
            kind='primary'
            type={SizeButton.LARGE}
            onClick={handleGetMagicCode}
            disabled={isDisabled}
            isLoading={isSubmitting}
            style={{
                ...(isSubmitting ? { background: `${lightTheme.colors.primary}!important` } : {}),
                transition: 'none',
                ':disabled': {
                 backgroundColor: `${lightTheme.colors.grayScale50}`,
                 color: `${lightTheme.colors.grayScale200}`,
                },
              }}
          >
            {t('Get magic code')}
          </ButtonCustom>

          {/* Info Text */}
          <h4
            style={{
              marginBottom: 0,
              marginTop: '8px',
              ...lightTheme.typography.Small12medium,
              color: lightTheme.colors.grayScale400,
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            {t("You'll get a one-time code by email to sign in.")}
          </h4>

          {onSignInWithPassword && (
            <h4
              style={{
                marginBottom: 0,
                marginTop: '24px',
                ...lightTheme.typography.Small12medium,
                color: lightTheme.colors.grayScale400,
                fontWeight: 400,
                textAlign: 'center',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1,
              }}
              onClick={() => {
                if (isSubmitting) return
                onSignInWithPassword()
              }}
            >
              {t('Sign in with Email & Password')}
            </h4>
          )}
        </>
      ) : (
        <SignInWithEmailVerifyCode
          email={email}
          onBack={() => setShowVerifyCode(false)}
          onSuccess={onSuccess}
        />
      )}
    </div>
  )
}

export default SignInWithEmailScreen
